/**
 * @name UserLayout
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Este componente é o layout principal do usuário com fundo gradiente animado.
 * @param {React.ReactNode} props.children - Os elementos filhos do componente.
 * @returns {JSX.Element} - O JSX do componente.
 * @package src/components/layouts/UserLayout/UserLayout.tsx
 */

import { Box, CssBaseline } from '@mui/material';
import React, { useState } from 'react';
import { SecurityFooter } from '../../common';
import GradientBackground from '../../common/backgrounds/GradientBackground';
import UserAppBar from './components/UserAppBar';
import UserSideBar from './components/UserSideBar/UserSideBar';
import './UserLayout.css';

interface UserLayoutProps {
     children: React.ReactNode;
}

const UserLayout: React.FC<UserLayoutProps> = ({ children }) => {
     const [mobileOpen, setMobileOpen] = useState(false);
     const [desktopOpen, setDesktopOpen] = useState(false);

     const handleDrawerToggle = () => {
          setMobileOpen(!mobileOpen);
          setDesktopOpen(!desktopOpen);
     };

     return (
          <GradientBackground>
               <Box
                    sx={{
                         minHeight: '100vh', // Altura mínima para ocupar toda a tela
                         display: 'flex',
                         flexDirection: 'column',
                    }}
               >
                    <CssBaseline />
                    <UserAppBar handleDrawerToggle={handleDrawerToggle} />
                    <Box sx={{ display: 'flex', flex: 1 }}>
                         <UserSideBar
                              mobileOpen={mobileOpen}
                              desktopOpen={desktopOpen}
                              handleDrawerToggle={handleDrawerToggle}
                         />
                         <Box
                              className={`main-content ${mobileOpen || desktopOpen ? 'content-darken' : ''}`}
                              component='main'
                              sx={{
                                   flexGrow: 1,
                                   width: { sm: desktopOpen ? 'calc(100% - 280px)' : '100%' },
                                   transition: 'width 0.3s ease',
                                   overflow: 'auto',
                                   display: 'flex',
                                   flexDirection: 'column',
                              }}
                         >
                              <Box sx={{ flex: 1 }}>{children}</Box>
                              <SecurityFooter />
                         </Box>
                    </Box>
               </Box>
          </GradientBackground>
     );
};

export default UserLayout;
